import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { roleHasPermission, Permissions } from 'lib/permissions/roles';
import { OccupationType, RoleType } from '../../enums';
import { ESnapshotExists, EUser } from '../../types';

/**
 * Get a user's role based on their occupation and active organization.
 * This is only safe on the frontend because of the reliance on activeOrganization
 */
export const getRole = (user: ESnapshotExists<EUser>) => {
  const { occupation, activeOrganization, roles, postRegistrationComplete } =
    user.data();

  try {
    // Individuals always have admin permissions for their own account
    if (occupation === OccupationType.individual.value) {
      return RoleType.admin.value;
    }

    if (!activeOrganization) {
      if (!postRegistrationComplete) {
        throw new Error(
          `Cannot get role for user with incomplete registration ${user.id}`
        );
      }
      throw new Error(
        `Cannot get role for user without activeOrganization set ${user.id}`
      );
    }

    if (!roles) {
      throw new Error(`Cannot get role for user without roles set ${user.id}`);
    }

    const permissionsForOrg = roles[activeOrganization.id];

    if (permissionsForOrg) {
      return permissionsForOrg;
    }

    throw new Error(
      `Unable to get role for user ${user.id} in organization ${activeOrganization?.id}`
    );
  } catch (err) {
    console.error(err);
    return 0;
  }
};

const getRolesForAllAllowedOrganizationsForPublisherUser = (
  user: ESnapshotExists<EUser>
): ResponseOrError<Map<string, number>> => {
  const { allowedOrganizations, roles, postRegistrationComplete } = user.data();

  const rolesMap: Map<string, number> = new Map();

  if (!postRegistrationComplete) {
    return wrapError(
      new Error(
        `Cannot get roles for user with incomplete registration ${user.id}`
      )
    );
  }

  if (!allowedOrganizations || allowedOrganizations.length === 0) {
    return wrapError(
      new Error(
        `Cannot get roles for user without allowedOrganizations set ${user.id}`
      )
    );
  }

  if (!roles) {
    return wrapError(
      new Error(`Cannot get roles for user without roles set ${user.id}`)
    );
  }

  for (const orgRef of allowedOrganizations) {
    const orgId = orgRef.id;
    const roleForOrg = roles[orgId];

    if (roleForOrg !== undefined) {
      rolesMap.set(orgId, roleForOrg);
    }
  }

  return wrapSuccess(rolesMap);
};

/**
 * Check if a publisher user has a given permission across all of their allowed organizations.
 */
export const getRoleHasPermissionForAllAllowedOrganizationsForPublisherUser = (
  user: ESnapshotExists<EUser>,
  permission: Permissions
): ResponseOrError<Set<string>> => {
  const [rolesError, rolesMap] =
    getRolesForAllAllowedOrganizationsForPublisherUser(user);

  if (rolesError) {
    return wrapSuccess(new Set());
  }

  const hasPermissionSet: Set<string> = new Set();

  for (const [orgId, role] of rolesMap.entries()) {
    if (roleHasPermission(role, permission)) {
      hasPermissionSet.add(orgId);
    }
  }

  return wrapSuccess(hasPermissionSet);
};
